<template>
  <div class="ideas-content">
    <h2
      class="mt-0 text-left slide-title-style"
      v-if="slide.title"
      v-html="dataValue"
    />
    <p
      class="mb-3 text-left title-desc slide-title-style"
      v-if="slide.titleDesc"
    >
      {{ slide.titleDesc }}
    </p>
    <div
      class="audio-control-slide"
      v-if="slide.audioLink
        && slide.audioPlayOnAudience && slide.id"
    >
      <AudioControl />
    </div>
    <div class="slide-img-wrapper" v-if="slide.image">
      <img
        :src="slide.image.large"
        alt
        class="slide-img"
        ref="slide-imgg"
      >
    </div>

    <p class="mb-3 text-center vote-text">
      <span v-if="slide.votingStep !== voteStep.RESULT">
        <span v-if="getVoteLeft() < 2">{{ $t('voteLeft', { msg: getVoteLeft() }) }}</span>
        <span v-else>{{ $t('votesLeft', { msg: getVoteLeft() }) }}</span>
      </span>
      <span v-else>{{ $t('votingClosed') }}</span>
    </p>

    <div class="ideas-tabs horizontal layout center center-justified">
      <div class="horizontal layout">
        <div class="idea-tab" :class="{'active': tab === 'all'}">
          <div class="tab-name" @click="selectTab('all')">
            {{ $t('allTab') }} <span v-if="countAll">({{ countAll | formatTotalPoint }})</span>
          </div>
        </div>
        <div class="idea-tab" :class="{'active': tab === 'my-idea'}">
          <div class="tab-name" @click="selectTab('my-idea')">
            {{ $t('mySubmissionTab') }} <span v-if="countMyIdea">
              ({{ countMyIdea | formatTotalPoint }})
            </span>
          </div>
        </div>
        <div class="idea-tab" :class="{'active': tab === 'voted'}">
          <div class="tab-name" @click="selectTab('voted')">
            {{ $t('votedTab') }} <span>({{ countVoted }}/{{ slide.limitChoice }}) </span>
          </div>
        </div>
      </div>
    </div>
    <p
      v-if="tab === 'voted' && slide.votingStep !== voteStep.RESULT && showTextVoted"
      class="idea-title"
    >
      {{ $t('canUnvote') }}
    </p>
    <div class="ideas-list">
      <div
        class="mb-4 horizontal layout center center-justified"
        v-if="loadingIdea && !loadDataError"
      >
        <Loading />
      </div>
      <div class="mb-4 text-center text-error" v-if="loadDataError">
        Something Went Wrong
      </div>
      <IdeaItem
        v-for="(item, index) in ideasShowOnTab"
        :item="item"
        :key="index"
        :index="index"
        :sort-ideas="sortIdeas"
        :get-vote-left="getVoteLeft"
        :is-disable-vote="isDisableVote"
      />
    </div>

    <ui-button
      block
      size="lg"
      @click="seeResult"
      v-if="slide.type === 'ideas' &&
        presentation.audiencePacing && slide.votingStep !== voteStep.RESULT"
      variant="outline-dark"
    >
      {{ $t('seeResult') }}
    </ui-button>
  </div>
</template>
<script>
import AudioControl from '@/components/AudioControl.vue'
import SlideOptionApi from '@/api/slide-option'
import * as Sentry from '@sentry/vue'
import { mapGetters, mapState } from 'vuex'
import util from '@/util'
import storage from '@/services/storage'
import EventBus from '@/eventBus'
import VoteStep from '@/constants/VoteStep'
import IdeaItem from '@/components/types/IdeaItem'
import UiButton from '@/components/UI/Button'
import Loading from '@/components/Loading'
import ClickableTitleMixin from '@/mixins/ClickableTitleMixin'

export default {
  components: {
    AudioControl,
    IdeaItem,
    UiButton,
    Loading,
  },

  mixins: [ClickableTitleMixin],

  data() {
    return {
      tab: 'all',
      ideas: [],
      votingIdea: null,
      ideasShowOnTab: [],
      countAll: 0,
      countMyIdea: 0,
      countVoted: 0,
      isDisableVote: false,
      voteStep: VoteStep,
      loadingIdea: true,
      showTextVoted: false,
      loadDataError: false,
    }
  },

  computed: {
    ...mapGetters({
      slide: 'activeActivity',
    }),
    ...mapState({
      presentation: (state) => state.presentation,
      audienceId: (state) => state.audienceId,
    }),
  },

  mounted() {
    this._getSlideOptions()
    EventBus.$on('update-voting-step', () => {
      this.sortIdeas()
    })

    EventBus.$on('vote-idea', (socketData) => {
      this._handleVoteIdea(socketData)
    })
  },

  destroyed() {
    EventBus.$off('update-voting-step')
    EventBus.$off('vote-idea')
  },

  methods: {
    seeResult() {
      this.$store.dispatch('updateSlide', {
        votingStep: 'result',
      })
      this.sortIdeas()
      util.mixpanelTrack(
        'Audience > Ideas',
        'Audience > Click see result',
        '',
      )
    },
    getVoteLeft() {
      const countVoted = this.ideas.filter((item) => {
        return item.voted
      }).length

      if (!this.slide.limitChoice) this.slide.limitChoice = 0
      let voteLeft = this.slide.limitChoice - countVoted
      if (voteLeft < 0) voteLeft = 0
      if (!voteLeft) {
        this.isDisableVote = true
      } else {
        this.isDisableVote = false
      }
      if (countVoted) {
        this.showTextVoted = true
      } else {
        this.showTextVoted = false
      }
      return voteLeft
    },
    _handleVoteIdea(socketData) {
      const index = this.ideas.findIndex((item) => {
        return item.id === socketData.slideOptionId
      })
      if (index === -1) return
      this.ideas[index].votesCount = socketData.numberOfVotes
      this.sortIdeas()
    },
    sortIdeas() {
      let ideas = [...this.ideas]

      if (this.slide.votingStep === VoteStep.VOTING) {
        ideas.sort((a, b) => {
          return new Date(a.createdAt) - new Date(b.createdAt)
        })
      }

      if (this.slide.votingStep === VoteStep.RESULT) {
        ideas.sort((a, b) => b.votesCount - a.votesCount)
      }

      ideas.forEach((idea, index) => {
        if (index === 0) {
          idea.position = 1
          return
        }

        const previousIdea = ideas[index - 1]
        if (idea.votesCount === previousIdea.votesCount) {
          idea.position = previousIdea.position
        } else {
          idea.position = index + 1
        }
      })

      if (this.tab === 'my-idea') {
        ideas = ideas.filter((item) => item.audience === this.audienceId)
      }

      if (this.tab === 'voted') {
        ideas = ideas.filter((item) => item.voted)
      }

      this.countAll = this.ideas.length
      this.countMyIdea = this.ideas.filter((item) => item.audience === this.audienceId).length
      this.countVoted = this.ideas.filter((item) => item.voted).length

      this.ideasShowOnTab = ideas
    },
    selectTab(tabName) {
      this.tab = tabName
      this.sortIdeas()
      util.mixpanelTrack(
        'Audience',
        `Audience > Ideas > Switch tab ${this.tab}`,
        '',
      )
    },
    async _getSlideOptions() {
      const query = {
        pageSize: 250,
        slideId: this.slide.id,
      }
      try {
        const resp = await SlideOptionApi.getSlideOptions(query)
        const ideasVotedIds = storage.getValueByPresentation(this.presentation.id, 'ideasVotedIds', [])

        this.ideas = resp.rows.map((item, key) => {
          item.index = key + 1
          item.color = util.getAhaColor(key)
          item.voted = ideasVotedIds.includes(item.id)
          return item
        })
        this.sortIdeas()
      } catch (error) {
        Sentry.captureException(error)
        this.loadDataError = true
      }
      this.loadingIdea = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";
.vote-text {
  font-size: 14px;
}
.idea-title {
  padding-bottom: 16px;
  text-align: center;
  margin: 0;
}
.ideas-tabs {
  border-bottom: 1px solid #D3D7E1;
  margin-bottom: 16px;

  .idea-tab {
    text-align: center;
    .tab-name {
      font-size: 14px;
      display: inline-block;
      padding: 16px;
      border-bottom: 3px solid transparent;
      position: relative;
      top: 2px;
      cursor: pointer;
      font-weight: bold;
      span {
        opacity: 0.6;
      }
    }

    &.active {
      .tab-name {
        color: $primary-color;
        border-color: $primary-color;

        span {
          color: #89BEFA;
          opacity: 1;
        }
      }
    }
  }
}

@media (max-width: 367px) {
  .ideas-tabs {
    .idea-tab {
      .tab-name {
        padding: 16px 12px;
      }
    }
  }
}

@media (max-width: 340px) {
  .ideas-tabs {
    .idea-tab {
      .tab-name {
        padding: 16px 8px;
      }
    }
  }
}

.text-error {
  color: $danger-color;
}
</style>
